import React from 'react'
import { Element, Button, Message, Columns, Tag } from 'react-bulma-components'
import { Form, Formik, Field } from 'formik'
import { useAddTagsToWorkPeriodsMutation } from '../../../../queries/work-periods'
import {
  useWorkPeriodTagsQuery,
  useCreateMutation as useCreateTagMutation,
  invalidateWorkPeriodTagsQueries,
} from '../../../../queries/work-period-tags'
import MultiSelectCreatableField from '../../../form/fields/multi-select-creatable'
import InputField from '../../../form/fields/input'
import { RequestButton } from '../../../request-components/request-components'
import {
  ApplyTagsToMultipleFormProps,
  ApplyToMultipleFormValues,
  CreateTagConfirmation,
  AddedTagsState,
} from './types'
import { convertTagsToOptions, handleTagCreation } from './utils'
import CreateTagConfirmationComponent from './create-tag-confirmation'
import { dateToDateInputValue, formatDate } from '../../../../utils/date'
import { WorkPeriodTag } from '../../../../api/work-period-tags'
import { endOfDay, startOfDay } from 'date-fns'

/**
 * Form component for applying tags to multiple work periods in a date range
 * Allows adding tags one by one and applying them to multiple work periods
 */
const ApplyTagsToMultipleForm: React.FC<ApplyTagsToMultipleFormProps> = ({
  tags,
  onSubmit,
  onCancel,
  defaultFormDate,
  defaultToDate,
  minDate,
  maxDate,
  dateRange,
}) => {
  const addTagsToWorkPeriodsMutation = useAddTagsToWorkPeriodsMutation()
  const createTagMutation = useCreateTagMutation()
  const { data: tagsData, isLoading: isLoadingTags } = useWorkPeriodTagsQuery()
  const selectRef = React.useRef<any>(null)
  const [createTagConfirmation, setCreateTagConfirmation] =
    React.useState<CreateTagConfirmation | null>(null)
  const [addedTags, setAddedTags] = React.useState<AddedTagsState>({
    addedTagIds: tags || [],
  })

  const availableTags = tagsData || []

  /**
   * Handle adding a tag to the session list
   */
  const handleAddTag = (tag: WorkPeriodTag) => {
    if (!addedTags.addedTagIds.find(t => t._id === tag._id)) {
      setAddedTags(prev => ({ addedTagIds: [...prev.addedTagIds, tag] }))
    }
  }

  /**
   * Handle removing a tag from the session list
   */
  const handleRemoveTag = (tag: WorkPeriodTag) => {
    setAddedTags(prev => ({ addedTagIds: prev.addedTagIds.filter(t => t._id !== tag._id) }))
  }

  /**
   * Handle tag selection/creation
   */
  const handleTagSelect = async (values: ApplyToMultipleFormValues, setFieldValue: any) => {
    if (!values.tagToApply) return

    // Handle both string and object values from MultiSelectCreatableField
    const tagValue =
      typeof values.tagToApply === 'string'
        ? values.tagToApply
        : values.tagToApply.value || values.tagToApply

    const selectedTag = availableTags.find(tag => tag._id === tagValue)

    if (selectedTag) {
      // Existing tag selected
      handleAddTag(selectedTag)
      setFieldValue('tagToApply', '') // Clear the field
    } else {
      // New tag creation needed
      handleTagCreation(
        typeof tagValue === 'string' ? tagValue : tagValue.value,
        availableTags,
        setCreateTagConfirmation,
        () => selectRef.current?.blur(),
      )
    }
  }

  /**
   * Handle form submission - apply all session tags to selected work periods
   */
  const handleSubmit = async (values: ApplyToMultipleFormValues) => {
    onSubmit({
      form: values.applyToOthersDateFrom
        ? startOfDay(new Date(values.applyToOthersDateFrom))
        : startOfDay(new Date()),
      to: values.applyToOthersDateTo
        ? endOfDay(new Date(values.applyToOthersDateTo))
        : endOfDay(new Date()),
      tags: addedTags.addedTagIds,
    })
  }

  /**
   * Handle creation of a new tag with similarity check
   */
  const handleCreateTag = async (inputValue: string) => {
    handleTagCreation(inputValue, availableTags, setCreateTagConfirmation, () =>
      selectRef.current?.blur(),
    )
  }

  /**
   * Confirm creation of new tag
   */
  const confirmCreateTag = async () => {
    if (!createTagConfirmation) return

    createTagMutation.mutate(
      {
        name: createTagConfirmation.newTag,
      },
      {
        onSuccess: response => {
          const newTag = response.data
          handleAddTag(newTag)
          setCreateTagConfirmation(null)
          selectRef.current?.blur()
          invalidateWorkPeriodTagsQueries()
        },
      },
    )
  }

  const initialValues: ApplyToMultipleFormValues = {
    tagToApply: '',
    applyToOthersDateFrom: dateToDateInputValue(new Date(defaultFormDate || new Date())),
    applyToOthersDateTo: dateToDateInputValue(new Date(defaultToDate || new Date())),
  }

  const tagOptions = convertTagsToOptions(availableTags)

  return (
    <Element mb={5}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={() => {
          const errors: Record<string, string> = {}
          return errors
        }}
      >
        {({ values, setFieldValue, isValid }) => {
          const workPeriodsIds =
            values.applyToOthersDateFrom && values.applyToOthersDateTo && dateRange
              ? `${formatDate(new Date(values.applyToOthersDateFrom))} - ${formatDate(new Date(values.applyToOthersDateTo))}`
              : ''
          return (
            <Form>
              {/* Display tags to be applied */}
              {addedTags.addedTagIds.length > 0 && (
                <Element mb={3} data-test="tags-to-add">
                  <Element mb={2}>
                    <strong>Catégorie(s) à ajouter :</strong>
                  </Element>
                  <Tag.Group>
                    {addedTags.addedTagIds.map(tag => {
                      return tag ? (
                        <Tag key={tag._id} size="medium">
                          {tag.name}
                          <Button
                            size="small"
                            color="ghost"
                            onClick={() => handleRemoveTag(tag)}
                            pr={1}
                          >
                            ×
                          </Button>
                        </Tag>
                      ) : null
                    })}
                  </Tag.Group>
                </Element>
              )}

              {!createTagConfirmation && (
                <>
                  <Element mb={3}>
                    <Field
                      label="Ajouter une catégorie"
                      name="tagToApply"
                      component={MultiSelectCreatableField}
                      options={tagOptions}
                      isMulti={false}
                      onCreateOption={(inputValue: string) => handleCreateTag(inputValue)}
                      isLoading={isLoadingTags || createTagMutation.isLoading}
                      selectRef={selectRef}
                      help="Sélectionnez une catégorie existante ou créez-en une nouvelle en tapant son nom"
                      onChange={(value: string | { value: string; label: string }) => {
                        if (value) {
                          const tagValue = typeof value === 'string' ? value : value.value
                          handleTagSelect({ ...values, tagToApply: tagValue }, setFieldValue)
                        }
                      }}
                    />
                  </Element>

                  {dateRange && (
                    <Columns>
                      <Columns.Column>
                        <Field
                          name="applyToOthersDateFrom"
                          component={InputField}
                          label="Du"
                          type="date"
                          min={dateToDateInputValue(minDate || new Date())}
                          max={
                            values.applyToOthersDateTo ||
                            dateToDateInputValue(maxDate || new Date())
                          }
                        />
                      </Columns.Column>
                      <Columns.Column>
                        <Field
                          name="applyToOthersDateTo"
                          component={InputField}
                          label="Au"
                          type="date"
                          min={
                            values.applyToOthersDateFrom ||
                            dateToDateInputValue(minDate || new Date())
                          }
                          max={maxDate ? dateToDateInputValue(maxDate) : undefined}
                        />
                      </Columns.Column>
                    </Columns>
                  )}
                </>
              )}

              {/* Preview du nombre de work periods concernés */}
              {values.applyToOthersDateFrom &&
                values.applyToOthersDateTo &&
                dateRange &&
                !createTagConfirmation && (
                  <Element mt={2}>
                    {(() => {
                      const startDate = new Date(values.applyToOthersDateFrom)
                      const endDate = new Date(values.applyToOthersDateTo)
                      return (
                        <Message color="info">
                          <Message.Body>
                            <Element renderAs="strong">
                              {dateRange ? `${formatDate(startDate)} - ${formatDate(endDate)}` : ''}
                            </Element>
                            <br /> Les catégoies peuvent être appliquées uniquement à des journées
                            de travail non validées.
                          </Message.Body>
                        </Message>
                      )
                    })()}
                  </Element>
                )}

              {/* Warning message for similar tags */}
              {createTagConfirmation && (
                <CreateTagConfirmationComponent
                  createTagConfirmation={createTagConfirmation}
                  isLoading={createTagMutation.isLoading}
                  onCancel={() => {
                    setCreateTagConfirmation(null)
                  }}
                  onConfirmCreate={confirmCreateTag}
                />
              )}

              {!createTagConfirmation && (
                <Element display="flex" justifyContent="flex-end" mt={4}>
                  {onCancel && (
                    <Button color="light" onClick={onCancel} mr={2}>
                      Annuler
                    </Button>
                  )}
                  <RequestButton
                    color="primary"
                    type="submit"
                    disabled={
                      !isValid ||
                      (workPeriodsIds.length === 0 && dateRange) ||
                      addedTags.addedTagIds.length === 0
                    }
                    isLoading={addTagsToWorkPeriodsMutation.isLoading}
                    data-test="submit-add-tags-to-work-periods"
                  >
                    Ajouter
                  </RequestButton>
                </Element>
              )}
            </Form>
          )
        }}
      </Formik>
    </Element>
  )
}

export default ApplyTagsToMultipleForm
